import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FileDownloadOutlined as DownloadIcon
} from '@mui/icons-material';
import VehicleListTable from 'src/components/vehicles/VehicleListTable';
import NewVehicle from 'src/components/vehicles/NewVehicle';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import API_URL from 'src/api/apiConstants';
import { BASEAPI } from 'src/api/api';
import moment from 'moment';
import { DATE_FORMAT } from 'src/utils/constants';
import { setErrorInfo } from 'src/redux/modalSlice';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';

const Vehicles = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();

  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);

  const handleSearch = (e) => setSearchText(e.target.value);

  const handleExport = () => {
    setShowExportConfirm(false);
    setLoading(true);
    BASEAPI.get(`${API_URL.vehicleList}?export=true`, {
      responseType: 'blob',
      timeout: 20000
    })
      .then((res) => {
        const fileName = `vehicles_${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
      })
      .catch(() => {
        dispatch(setErrorInfo({ errorMsg: 'Error downloading report' }));
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Helmet>
        <title>Vehicles | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* ---------- HEADER (SAME AS SITES) ---------- */}
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                VEHICLES
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Manage vehicles, fuel usage, and assignments.
              </Typography>
            </Box>

            {/* Right */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap', lg: 'nowrap' }}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <TextField
                placeholder="Search vehicles..."
                size="small"
                value={searchText}
                onChange={handleSearch}
                sx={{
                  width: {
                    xs: '100%', sm: 240, md: 260, xl: 320
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={setSiteFilter}
                  sx={{ width: { xs: '100%', sm: 170, md: 180 }, mr: 0 }}
                />
              )}

              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={() => setShowExportConfirm(true)}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {isLoading ? 'Exporting...' : 'Export'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  backgroundColor: theme.palette.primary.main,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Add Vehicle
              </Button>
            </Stack>
          </Stack>

          {/* ---------- CONTENT ---------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <VehicleListTable
              searchText={searchText}
              siteFilter={siteFilter}
            />
          </Box>

          <NewVehicle
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ConfirmationDialog
            open={showExportConfirm}
            info="Are you sure you want to export vehicles report?"
            actionName="Export"
            onClose={() => setShowExportConfirm(false)}
            onSubmit={handleExport}
          />
        </Container>
      </Box>
    </>
  );
};

Vehicles.propTypes = {};

export default Vehicles;
